/**
 * Loans: money the business lends out, and money it borrows.
 *
 * ── The accounting, stated once ─────────────────────────────────────────────
 *
 * A loan is not revenue and not an expense, in either direction.
 *
 *   - Lending (`receivable`) turns cash into a receivable. Principal coming
 *     back reduces the receivable; only the INTEREST is income (finance
 *     income).
 *   - Borrowing (`payable`) turns a liability into cash. Principal paid back
 *     reduces the liability; only the INTEREST is a cost (finance expense).
 *
 * So nothing in this module ever writes to `sales` or `expenses`, which is
 * what the dashboard's Revenue, Expenses and Profit read. Cash movements are
 * reported separately, as cash flow.
 *
 * ── Money ──────────────────────────────────────────────────────────────────
 *
 * Every amount is a 2-decimal string on the wire and a bigint of minor units
 * in arithmetic (see `money.ts`). Interest is the one place this codebase has
 * to ROUND, because a rate times a balance is rarely a whole kobo: it rounds
 * half up, once per instalment, and the last instalment absorbs whatever is
 * left so the schedule always sums exactly to its totals.
 *
 * The schedule and the allocation of a payment live here, in the shared
 * package, so the form's preview, the API that saves the loan and the tests
 * cannot compute three different answers.
 */

import { z } from 'zod';
import {
  dateRangeFields,
  isOrderedRange,
  isoDateSchema,
  longTextSchema,
  moneySchema,
  nonNegativeMoneySchema,
  ORDERED_RANGE_ISSUE,
  paginationSchema,
  phoneSchema,
  positiveMoneySchema,
  shortTextSchema,
  uuidSchema,
} from '../primitives.js';
import { PAYMENT_METHODS } from '../enums.js';
import { toDecimalString, toMinor } from '../money.js';

// ── Vocabulary ──────────────────────────────────────────────────────────────

/** Which way the money owed runs. */
export const LOAN_DIRECTIONS = ['receivable', 'payable'] as const;
export type LoanDirection = (typeof LOAN_DIRECTIONS)[number];
export const LOAN_DIRECTION_LABELS: Record<LoanDirection, string> = {
  receivable: 'Lent out',
  payable: 'Borrowed',
};

export const LOAN_CATEGORIES = ['rider', 'vehicle', 'staff', 'operational'] as const;
export type LoanCategory = (typeof LOAN_CATEGORIES)[number];
export const LOAN_CATEGORY_LABELS: Record<LoanCategory, string> = {
  rider: 'Rider loans',
  vehicle: 'Vehicle loans',
  staff: 'Staff loans',
  operational: 'Operational loans',
};

/** Who is on the other side. A rider or staff member links to their record. */
export const LOAN_COUNTERPARTY_KINDS = ['rider', 'staff', 'individual', 'organisation'] as const;
export type LoanCounterpartyKind = (typeof LOAN_COUNTERPARTY_KINDS)[number];
export const LOAN_COUNTERPARTY_LABELS: Record<LoanCounterpartyKind, string> = {
  rider: 'Rider',
  staff: 'Staff member',
  individual: 'Individual',
  organisation: 'Organisation',
};

/**
 * `flat`: interest on the ORIGINAL principal for the whole term, spread evenly.
 * `reducing`: interest on the balance still owed each period (an amortising
 * loan with equal instalments). The rate is always a yearly percentage.
 */
export const LOAN_INTEREST_TYPES = ['none', 'flat', 'reducing'] as const;
export type LoanInterestType = (typeof LOAN_INTEREST_TYPES)[number];
export const LOAN_INTEREST_TYPE_LABELS: Record<LoanInterestType, string> = {
  none: 'Interest-free',
  flat: 'Fixed (flat rate)',
  reducing: 'Reducing balance',
};

export const LOAN_FREQUENCIES = ['daily', 'weekly', 'biweekly', 'monthly', 'custom'] as const;
export type LoanFrequency = (typeof LOAN_FREQUENCIES)[number];
export const LOAN_FREQUENCY_LABELS: Record<LoanFrequency, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Every two weeks',
  monthly: 'Monthly',
  custom: 'Custom interval',
};

/**
 * What is stored. `overdue` is not here: it is a fact about the schedule and
 * today's date, derived on read, so it can never be stale.
 */
export const LOAN_STORED_STATUSES = ['draft', 'active', 'completed', 'cancelled', 'written_off'] as const;
export type LoanStoredStatus = (typeof LOAN_STORED_STATUSES)[number];

/** What is shown. */
export const LOAN_STATUSES = ['draft', 'active', 'overdue', 'completed', 'cancelled', 'written_off'] as const;
export type LoanStatus = (typeof LOAN_STATUSES)[number];
export const LOAN_STATUS_LABELS: Record<LoanStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  overdue: 'Overdue',
  completed: 'Completed',
  cancelled: 'Cancelled',
  written_off: 'Written off',
};

/**
 * The ledger's entries. Append-only: a mistake is corrected by a `reversal`
 * that points at the entry it undoes, never by editing or deleting it.
 */
/**
 * `top_up` is a further amount paid out on a loan already issued; `adjustment`
 * is a balance correction that moves no money and is neither a repayment nor a
 * write-off. See db/sql/0133.
 */
export const LOAN_TRANSACTION_KINDS = [
  'disbursement',
  'repayment',
  'reversal',
  'write_off',
  'interest_rebate',
  'top_up',
  'adjustment',
] as const;
export type LoanTransactionKind = (typeof LOAN_TRANSACTION_KINDS)[number];

/**
 * Early settlement. Fixed on the loan when it is created, and never changed
 * after it is issued, so a settlement always follows the terms agreed.
 *
 * `no_rebate`: settling early pays everything scheduled, interest included —
 * the default, and the only policy that never changes what was agreed.
 * `rebate_future_interest` (full waiver): the unpaid interest on every
 * instalment not yet due is waived in full.
 * `rebate_prorated_daily` (daily pro-rata): interest is owed for the days
 * that have passed in the period in progress, and nothing for the days that
 * have not; later periods are waived in full. See `settlementQuote`.
 *
 * Any waiver is its own ledger entry (`interest_rebate`), never netted into
 * the payment, so the books show both what was owed and what was forgiven.
 * Which of these a business may offer in a country is part of its lending
 * policy (`earlySettlementOptions`), declared by the owner, not by CenoX.
 */
export const LOAN_EARLY_REPAYMENT_POLICIES = ['no_rebate', 'rebate_future_interest', 'rebate_prorated_daily'] as const;
export type LoanEarlyRepaymentPolicy = (typeof LOAN_EARLY_REPAYMENT_POLICIES)[number];
export const LOAN_EARLY_REPAYMENT_LABELS: Record<LoanEarlyRepaymentPolicy, string> = {
  no_rebate: 'No rebate: all scheduled interest is due',
  rebate_future_interest: 'Full waiver: interest on instalments not yet due is waived',
  rebate_prorated_daily: 'Daily pro-rata: interest only for the days that have passed',
};

/** How a processing fee is collected. */
export const LOAN_FEE_COLLECTIONS = ['upfront', 'first_instalment'] as const;
export type LoanFeeCollection = (typeof LOAN_FEE_COLLECTIONS)[number];
export const LOAN_FEE_COLLECTION_LABELS: Record<LoanFeeCollection, string> = {
  upfront: 'Taken when the loan is paid out',
  first_instalment: 'Added to the first instalment',
};

/** An optional penalty on a late instalment. Applied by a person, never automatically. */
export const LOAN_LATE_FEE_TYPES = ['none', 'fixed', 'percent'] as const;
export type LoanLateFeeType = (typeof LOAN_LATE_FEE_TYPES)[number];
export const LOAN_LATE_FEE_LABELS: Record<LoanLateFeeType, string> = {
  none: 'No late fee',
  fixed: 'Fixed amount per late instalment',
  percent: 'Percentage of the late instalment',
};

export const LOAN_CHARGE_KINDS = ['processing_fee', 'late_fee'] as const;
export type LoanChargeKind = (typeof LOAN_CHARGE_KINDS)[number];

/**
 * Overpayments are refused. Stated as a constant, not a setting, so nothing —
 * no import, no policy edit, no default — can quietly start accepting more
 * money than is owed.
 */
export const LOAN_OVERPAYMENT_POLICY = 'refuse' as const;

/** Where a repayment came from. Deductions name the record they came out of. */
export const LOAN_REPAYMENT_SOURCES = ['manual', 'remittance', 'payout'] as const;
export type LoanRepaymentSource = (typeof LOAN_REPAYMENT_SOURCES)[number];
export const LOAN_REPAYMENT_SOURCE_LABELS: Record<LoanRepaymentSource, string> = {
  manual: 'Direct payment',
  remittance: 'Deducted at remittance',
  payout: 'Deducted from payout',
};

export const LOAN_DEDUCTION_SOURCES = ['remittance', 'payout'] as const;
export type LoanDeductionSource = (typeof LOAN_DEDUCTION_SOURCES)[number];

/** Offered on every business. Businesses may add their own. */
export const DEFAULT_LOAN_PURPOSES = [
  'Salary advance',
  'Fuel advance',
  'Vehicle financing',
  'Repairs',
  'Equipment',
  'Emergency',
  'Working capital',
] as const;

/** Longest schedule accepted: ten years of daily instalments is already absurd. */
export const MAX_LOAN_INSTALMENTS = 3650;
/** A yearly rate above this is almost certainly a typo for a monthly one. */
export const MAX_ANNUAL_RATE_PERCENT = 1000;

// ── Country lending policy ──────────────────────────────────────────────────

/**
 * What a business has declared about lending in one country.
 *
 * Entered by the business, never shipped as a default: a rate cap or a
 * licensing rule is a legal claim about a jurisdiction, and CenoX does not
 * make it. With no policy recorded, the only lending allowed is an ordinary
 * internal advance — interest-free, fee-free, to the business's own riders or
 * staff. Anything else (interest, fees, or lending to outsiders) needs a
 * policy with lending switched on and declared by a named person: the
 * business's own statement, recorded with who made it and when, never
 * verified by CenoX.
 * Borrowing by the business is never restricted here.
 */
export interface LendingPolicy {
  countryCode: string;
  /** Lending beyond internal advances is switched on. */
  lendingEnabled: boolean;
  /** Interest-free, fee-free advances to own riders and staff. On unless the business turns it off. */
  internalAdvancesAllowed: boolean;
  /** Highest yearly rate the business has declared it may charge. Null: none recorded. */
  maxAnnualRatePercent: string | null;
  /** Whether interest may be charged to the business's own staff. */
  staffInterestAllowed: boolean;
  /** Whether processing and late fees may be charged. */
  feesAllowed: boolean;
  /** Licence or registration the business lends under, if any. */
  licenceReference: string | null;
  complianceNotes: string | null;
  /**
   * The early-settlement terms the owner has declared the business may offer
   * in this country. At least one. A loan with interest must use one of them.
   */
  earlySettlementOptions: LoanEarlyRepaymentPolicy[];
  /** Preselected on a new loan; always one of the options. */
  defaultEarlySettlement: LoanEarlyRepaymentPolicy;
  /**
   * Who declared the policy, and when. Lending stays off until this is set.
   * A record of the business's own statement: CenoX does not verify it, and
   * it is not legal advice or approval.
   */
  confirmedByName: string | null;
  confirmedAt: string | null;
}

export const lendingPolicyInputSchema = z.object({
  countryCode: z.string().trim().length(2).toUpperCase(),
  lendingEnabled: z.boolean(),
  internalAdvancesAllowed: z.boolean().default(true),
  maxAnnualRatePercent: z
    .union([z.string(), z.number()])
    .transform((v) => String(v).trim())
    .refine((v) => RATE_RE.test(v), 'Enter a rate such as 24 or 2.5')
    .nullable()
    .optional(),
  staffInterestAllowed: z.boolean().default(false),
  feesAllowed: z.boolean().default(false),
  licenceReference: z.string().trim().max(200).nullable().optional(),
  complianceNotes: z.string().trim().max(2000).nullable().optional(),
  /**
   * Required when lending is switched on, with no default: which early
   * settlement terms the law lets this business offer is the owner's
   * statement to make, so CenoX never presumes one.
   */
  earlySettlementOptions: z.array(z.enum(LOAN_EARLY_REPAYMENT_POLICIES)).min(1).max(LOAN_EARLY_REPAYMENT_POLICIES.length).optional(),
  defaultEarlySettlement: z.enum(LOAN_EARLY_REPAYMENT_POLICIES).optional(),
  /** Required to switch lending on: the person saving declares the policy reflects the rules for this country. */
  confirmCompliance: z.boolean().default(false),
}).superRefine((v, ctx) => {
  const options = v.earlySettlementOptions ? [...new Set(v.earlySettlementOptions)] : undefined;
  if (v.lendingEnabled && !options) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['earlySettlementOptions'], message: 'Choose the early settlement terms you may offer' });
  }
  if (options && v.defaultEarlySettlement && !options.includes(v.defaultEarlySettlement)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['defaultEarlySettlement'], message: 'The default must be one of the terms you may offer' });
  }
});
export type LendingPolicyInput = z.infer<typeof lendingPolicyInputSchema>;

/** What a loan asks of the policy, for the check below and the form's warning. */
export interface LendingAsk {
  direction: LoanDirection;
  counterpartyKind: LoanCounterpartyKind;
  interestType: LoanInterestType;
  interestRate: string;
  processingFee: string;
  lateFeeType: LoanLateFeeType;
  earlyRepaymentPolicy: LoanEarlyRepaymentPolicy;
}

/** True for an interest-free, fee-free advance to the business's own rider or staff. */
export function isInternalAdvance(ask: LendingAsk): boolean {
  return (
    ask.direction === 'receivable' &&
    (ask.counterpartyKind === 'rider' || ask.counterpartyKind === 'staff') &&
    ask.interestType === 'none' &&
    toMinor(ask.processingFee || '0') === 0n &&
    ask.lateFeeType === 'none'
  );
}

/**
 * Why a loan may not be issued under this policy, or null when it may.
 *
 * Pure, so the form can warn before the API refuses, with the same words.
 */
export function lendingRefusal(ask: LendingAsk, policy: LendingPolicy | null, countryCode: string): string | null {
  if (ask.direction === 'payable') return null;
  if (isInternalAdvance(ask)) {
    if (policy && !policy.internalAdvancesAllowed) {
      return `Advances to riders and staff are switched off in the lending policy for ${countryCode}.`;
    }
    return null;
  }
  if (!policy || !policy.lendingEnabled || !policy.confirmedAt) {
    return `Loans with interest or fees, or to anyone outside the business, need a lending policy for ${countryCode} declared by the owner. Record one under Loans → Lending policy, or make this an interest-free advance to a rider or staff member.`;
  }
  if (ask.interestType !== 'none' && !policy.earlySettlementOptions.includes(ask.earlyRepaymentPolicy)) {
    return `The lending policy for ${countryCode} does not list "${LOAN_EARLY_REPAYMENT_LABELS[ask.earlyRepaymentPolicy]}" among the early settlement terms you may offer.`;
  }
  if (ask.interestType !== 'none' && ask.counterpartyKind === 'staff' && !policy.staffInterestAllowed) {
    return `The lending policy for ${countryCode} does not allow interest on loans to staff.`;
  }
  if ((toMinor(ask.processingFee || '0') > 0n || ask.lateFeeType !== 'none') && !policy.feesAllowed) {
    return `The lending policy for ${countryCode} does not allow loan fees.`;
  }
  if (
    ask.interestType !== 'none' &&
    policy.maxAnnualRatePercent !== null &&
    rateScaled(ask.interestRate) > rateScaled(policy.maxAnnualRatePercent)
  ) {
    return `The lending policy for ${countryCode} allows at most ${Number(policy.maxAnnualRatePercent)}% a year.`;
  }
  return null;
}

// ── Rates ──────────────────────────────────────────────────────────────────

/** A percentage with up to four decimal places, e.g. "24", "2.5", "18.7500". */
const RATE_RE = /^\d{1,4}(\.\d{1,4})?$/;
const RATE_SCALE = 1_000_000n; // percent × 10^4, and /100 for the fraction

export const ratePercentSchema = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? String(v) : v.trim()))
  .refine((v) => RATE_RE.test(v), 'Enter a rate such as 24 or 2.5 (up to four decimal places)')
  .refine(
    (v) => !RATE_RE.test(v) || Number(v) <= MAX_ANNUAL_RATE_PERCENT,
    `A yearly rate above ${MAX_ANNUAL_RATE_PERCENT}% is not accepted`,
  )
  .transform((v) => normalizeRate(v));

/** Canonical rate string with four decimals. */
export function normalizeRate(value: string): string {
  const scaled = rateScaled(value);
  const whole = scaled / 10_000n;
  const frac = scaled % 10_000n;
  return `${whole}.${frac.toString().padStart(4, '0')}`;
}

/** Percent × 10^4 as an integer: "24.5" → 245000n. */
function rateScaled(value: string): bigint {
  const [whole = '0', frac = ''] = value.trim().split('.');
  return BigInt(whole) * 10_000n + BigInt((frac + '0000').slice(0, 4));
}

/** Round a rational to the nearest integer, halves away from zero. */
function roundDiv(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n !== denominator < 0n;
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  const q = (n * 2n + d) / (2n * d);
  return negative ? -q : q;
}

/**
 * How many periods make a year, as a fraction (numerator, denominator), so a
 * custom 10-day interval is exactly 36.5 rather than a float.
 */
function periodsPerYear(frequency: LoanFrequency, intervalDays: number | null | undefined): [bigint, bigint] {
  switch (frequency) {
    case 'daily':
      return [365n, 1n];
    case 'weekly':
      return [52n, 1n];
    case 'biweekly':
      return [26n, 1n];
    case 'monthly':
      return [12n, 1n];
    case 'custom':
      return [365n, BigInt(Math.max(1, intervalDays ?? 1))];
  }
}

// ── Dates ──────────────────────────────────────────────────────────────────

function parseDate(iso: string): [number, number, number] {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return [y, m, d];
}

function formatDate(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function shiftDays(iso: string, days: number): string {
  const [y, m, d] = parseDate(iso);
  const at = new Date(Date.UTC(y, m - 1, d + days));
  return formatDate(at.getUTCFullYear(), at.getUTCMonth() + 1, at.getUTCDate());
}

/** Months forward, clamped: a loan due on the 31st falls due on 28 February. */
function shiftMonths(iso: string, months: number): string {
  const [y, m, d] = parseDate(iso);
  const total = y * 12 + (m - 1) + months;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return formatDate(year, month, Math.min(d, last));
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysFromTo(from: string, to: string): number {
  const [fy, fm, fd] = parseDate(from);
  const [ty, tm, td] = parseDate(to);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
}

/**
 * The due date of instalment `index` (0-based).
 *
 * Monthly instalments are anchored to the first due date rather than stepped,
 * so a schedule starting on the 31st returns to the 31st after February.
 */
export function loanDueDate(
  firstDueOn: string,
  index: number,
  frequency: LoanFrequency,
  intervalDays?: number | null,
): string {
  switch (frequency) {
    case 'daily':
      return shiftDays(firstDueOn, index);
    case 'weekly':
      return shiftDays(firstDueOn, index * 7);
    case 'biweekly':
      return shiftDays(firstDueOn, index * 14);
    case 'custom':
      return shiftDays(firstDueOn, index * Math.max(1, intervalDays ?? 1));
    case 'monthly':
      return shiftMonths(firstDueOn, index);
  }
}

// ── The schedule ────────────────────────────────────────────────────────────

export interface LoanTerms {
  principal: string;
  interestType: LoanInterestType;
  /** Yearly percentage. Ignored for `none`. */
  interestRate: string;
  frequency: LoanFrequency;
  intervalDays?: number | null;
  instalmentCount: number;
  firstDueOn: string;
  /** A one-off processing fee. Zero or absent: none. */
  processingFee?: string | null | undefined;
}

export interface ScheduledInstalment {
  /** 1-based. */
  sequence: number;
  dueOn: string;
  principal: string;
  interest: string;
  /** The processing fee, on the first instalment only. */
  fee: string;
  total: string;
}

export interface LoanSchedule {
  instalments: ScheduledInstalment[];
  totalPrincipal: string;
  totalInterest: string;
  totalFees: string;
  /** Principal, interest and fees. */
  totalPayable: string;
  /**
   * The regular instalment. Every instalment but the last is exactly this; the
   * last differs only by the rounding it absorbs.
   */
  instalmentAmount: string;
  finalDueOn: string;
}

/**
 * Build a repayment schedule.
 *
 *   - none: principal split evenly; the last instalment takes the remainder.
 *   - flat: interest = principal × rate × term in years, split evenly.
 *   - reducing: equal instalments (annuity). Each period's interest is the
 *     balance × the period rate, rounded half up; the rest of the instalment
 *     is principal; the last instalment clears whatever balance remains.
 *
 * Throws on terms that cannot produce a schedule; the input schema refuses
 * them first, so reaching the throw is a programming error.
 */
export function buildLoanSchedule(terms: LoanTerms): LoanSchedule {
  const principal = toMinor(terms.principal);
  const n = terms.instalmentCount;
  if (principal <= 0n) throw new Error('Principal must be positive');
  if (!Number.isInteger(n) || n < 1 || n > MAX_LOAN_INSTALMENTS) {
    throw new Error('Instalment count out of range');
  }

  const rate = terms.interestType === 'none' ? 0n : rateScaled(terms.interestRate);
  const [ppyNum, ppyDen] = periodsPerYear(terms.frequency, terms.intervalDays);
  const nBig = BigInt(n);

  const principalParts: bigint[] = [];
  const interestParts: bigint[] = [];

  if (rate === 0n || terms.interestType === 'none') {
    const each = principal / nBig;
    for (let i = 0; i < n; i++) {
      principalParts.push(i === n - 1 ? principal - each * (nBig - 1n) : each);
      interestParts.push(0n);
    }
  } else if (terms.interestType === 'flat') {
    // principal × (rate% / 100) × (n / periodsPerYear)
    //   = principal × rateScaled × n × ppyDen / (RATE_SCALE × ppyNum)
    const totalInterest = roundDiv(principal * rate * nBig * ppyDen, RATE_SCALE * ppyNum);
    const eachP = principal / nBig;
    const eachI = totalInterest / nBig;
    for (let i = 0; i < n; i++) {
      const last = i === n - 1;
      principalParts.push(last ? principal - eachP * (nBig - 1n) : eachP);
      interestParts.push(last ? totalInterest - eachI * (nBig - 1n) : eachI);
    }
  } else {
    // Period rate as a fraction: rateScaled × ppyDen / (RATE_SCALE × ppyNum).
    const rNum = rate * ppyDen;
    const rDen = RATE_SCALE * ppyNum;
    // The instalment itself is the only figure computed in floating point; it
    // is rounded to a whole minor unit and every later figure is exact. Any
    // drift lands on the last instalment, which clears the balance exactly.
    const r = Number(rNum) / Number(rDen);
    const payment = BigInt(
      Math.round((Number(principal) * r) / (1 - Math.pow(1 + r, -n))),
    );
    let balance = principal;
    for (let i = 0; i < n; i++) {
      const interest = roundDiv(balance * rNum, rDen);
      let principalPart = i === n - 1 ? balance : payment - interest;
      if (principalPart < 0n) principalPart = 0n;
      if (principalPart > balance) principalPart = balance;
      principalParts.push(principalPart);
      interestParts.push(interest);
      balance -= principalPart;
    }
  }

  const fee = toMinor(terms.processingFee || '0');
  if (fee < 0n) throw new Error('A fee cannot be negative');

  const instalments: ScheduledInstalment[] = principalParts.map((p, i) => {
    const interest = interestParts[i] ?? 0n;
    const f = i === 0 ? fee : 0n;
    return {
      sequence: i + 1,
      dueOn: loanDueDate(terms.firstDueOn, i, terms.frequency, terms.intervalDays),
      principal: toDecimalString(p),
      interest: toDecimalString(interest),
      fee: toDecimalString(f),
      total: toDecimalString(p + interest + f),
    };
  });

  const totalInterest = interestParts.reduce((a, b) => a + b, 0n);
  // The regular instalment is the second one when the first carries the fee.
  const regular = instalments[fee > 0n && instalments.length > 1 ? 1 : 0]!;
  return {
    instalments,
    totalPrincipal: toDecimalString(principal),
    totalInterest: toDecimalString(totalInterest),
    totalFees: toDecimalString(fee),
    totalPayable: toDecimalString(principal + totalInterest + fee),
    instalmentAmount: toDecimalString(toMinor(regular.principal) + toMinor(regular.interest)),
    finalDueOn: instalments[instalments.length - 1]!.dueOn,
  };
}

// ── Allocating a payment ────────────────────────────────────────────────────

export interface InstalmentBalance {
  id: string;
  sequence: number;
  /** Still owed on this instalment. */
  principalOwed: string;
  interestOwed: string;
  /** Processing and late fees still owed. */
  feeOwed?: string;
  /** Needed only for an early-settlement quote. */
  dueOn?: string;
  /** The instalment's scheduled interest, and the day its period began (the previous due date, or the issue date for the first). Needed only for a daily pro-rata quote. */
  interestScheduled?: string;
  periodStartsOn?: string;
}

export interface Allocation {
  instalmentId: string;
  principal: string;
  interest: string;
  fee: string;
}

/**
 * Split a payment across the schedule.
 *
 * Oldest instalment first, and within an instalment fees, then interest, then
 * principal — the order lenders use, and the one that keeps an early payment
 * from leaving charges stranded on an instalment already marked paid. An
 * early payment simply reaches instalments that are not yet due; scheduled
 * interest is not recomputed (see `settlementQuote` for early settlement).
 *
 * Returns null when the amount is more than everything still owed. There is no
 * overpayment policy, so the caller refuses it.
 */
export function allocatePayment(amount: string, schedule: InstalmentBalance[]): Allocation[] | null {
  let remaining = toMinor(amount);
  if (remaining <= 0n) return [];

  const owed = schedule.reduce(
    (sum, s) => sum + toMinor(s.principalOwed) + toMinor(s.interestOwed) + toMinor(s.feeOwed ?? '0'),
    0n,
  );
  if (remaining > owed) return null;

  const allocations: Allocation[] = [];
  for (const s of [...schedule].sort((a, b) => a.sequence - b.sequence)) {
    if (remaining === 0n) break;
    const take = (owedHere: bigint) => {
      const t = remaining < owedHere ? remaining : owedHere;
      remaining -= t;
      return t;
    };
    const fee = take(toMinor(s.feeOwed ?? '0'));
    const interest = take(toMinor(s.interestOwed));
    const principal = take(toMinor(s.principalOwed));
    if (fee > 0n || interest > 0n || principal > 0n) {
      allocations.push({
        instalmentId: s.id,
        principal: toDecimalString(principal),
        interest: toDecimalString(interest),
        fee: toDecimalString(fee),
      });
    }
  }
  return allocations;
}

/** How the interest of the instalment in progress was split by days, under daily pro-rata. */
export interface SettlementProration {
  instalmentId: string;
  periodStartsOn: string;
  dueOn: string;
  /** Days from the period's start to the settlement date, and in the whole period. */
  daysElapsed: number;
  periodDays: number;
  interestScheduled: string;
  /** Scheduled interest × daysElapsed ÷ periodDays, rounded to the nearest kobo/cent, halves up. */
  interestEarned: string;
}

export interface SettlementQuote {
  /** What settling in full on the day costs. */
  payoff: string;
  /** Interest waived under the loan's policy. Zero under `no_rebate`. */
  interestWaived: string;
  /** How the waiver lands on each instalment. */
  rebates: Allocation[];
  /** Under daily pro-rata, the instalment whose period is in progress. Null otherwise. */
  proration: SettlementProration | null;
}

/**
 * Settling a loan in full on `today`. Pure: the same balances, policy and day
 * always give the same figures, to the kobo.
 *
 * Per instalment:
 * - due on or before `today`: everything unpaid is owed;
 * - due after `today`: the unpaid principal and fees are owed, and its unpaid
 *   interest is
 *   - `no_rebate`: owed;
 *   - `rebate_future_interest`: waived in full;
 *   - `rebate_prorated_daily`: owed only for the days already passed in its
 *     period. earned = round(scheduled × elapsed ÷ periodDays), elapsed held
 *     between 0 and periodDays, so an instalment whose period has not begun
 *     earns nothing. The waiver is the unearned part (scheduled − earned),
 *     capped at what is still unpaid: interest already paid counts first
 *     against what was earned, and is never handed back.
 */
export function settlementQuote(
  balances: InstalmentBalance[],
  policy: LoanEarlyRepaymentPolicy,
  today: string,
): SettlementQuote {
  let payoff = 0n;
  let waived = 0n;
  const rebates: Allocation[] = [];
  let proration: SettlementProration | null = null;
  for (const b of [...balances].sort((x, y) => x.sequence - y.sequence)) {
    const principal = toMinor(b.principalOwed);
    const interest = toMinor(b.interestOwed);
    const fee = toMinor(b.feeOwed ?? '0');
    const future = b.dueOn !== undefined && b.dueOn > today;
    let waive = 0n;
    if (future && interest > 0n) {
      if (policy === 'rebate_future_interest') {
        waive = interest;
      } else if (policy === 'rebate_prorated_daily') {
        if (b.interestScheduled === undefined || b.periodStartsOn === undefined || b.dueOn === undefined) {
          throw new Error('A daily pro-rata quote needs each instalment\'s scheduled interest and period start');
        }
        const scheduled = toMinor(b.interestScheduled);
        const periodDays = daysFromTo(b.periodStartsOn, b.dueOn);
        const elapsed = Math.min(Math.max(daysFromTo(b.periodStartsOn, today), 0), Math.max(periodDays, 0));
        const earned = periodDays <= 0 ? scheduled : roundDiv(scheduled * BigInt(elapsed), BigInt(periodDays));
        const unearned = scheduled - earned;
        waive = unearned <= 0n ? 0n : unearned < interest ? unearned : interest;
        if (elapsed > 0 && elapsed < periodDays) {
          proration = {
            instalmentId: b.id,
            periodStartsOn: b.periodStartsOn,
            dueOn: b.dueOn,
            daysElapsed: elapsed,
            periodDays,
            interestScheduled: toDecimalString(scheduled),
            interestEarned: toDecimalString(earned),
          };
        }
      }
    }
    if (waive > 0n) {
      waived += waive;
      rebates.push({ instalmentId: b.id, principal: '0.00', interest: toDecimalString(waive), fee: '0.00' });
    }
    payoff += principal + interest - waive + fee;
  }
  return { payoff: toDecimalString(payoff), interestWaived: toDecimalString(waived), rebates, proration };
}

/** The late fee for one instalment under a loan's policy, rounded half up. */
export function lateFeeFor(
  type: LoanLateFeeType,
  value: string,
  instalmentAmount: string,
): string {
  if (type === 'none') return '0.00';
  // Stored at four decimals (it may be a percentage); a fixed fee has two that matter.
  if (type === 'fixed') return toDecimalString(toMinor(value.trim().replace(/^(\d+\.\d{2})0*$/, '$1').replace(/^(\d+)\.0+$/, '$1')));
  // value is a percentage with up to four decimals.
  return toDecimalString(roundDiv(toMinor(instalmentAmount) * rateScaled(value), RATE_SCALE));
}

// ── Inputs ─────────────────────────────────────────────────────────────────

const termsShape = {
  principal: positiveMoneySchema,
  interestType: z.enum(LOAN_INTEREST_TYPES).default('none'),
  interestRate: ratePercentSchema.default('0'),
  frequency: z.enum(LOAN_FREQUENCIES),
  intervalDays: z.coerce.number().int().min(1).max(365).nullable().optional(),
  instalmentCount: z.coerce.number().int().min(1).max(MAX_LOAN_INSTALMENTS),
  issuedOn: isoDateSchema,
  firstDueOn: isoDateSchema,
  processingFee: nonNegativeMoneySchema.default('0'),
  feeCollection: z.enum(LOAN_FEE_COLLECTIONS).default('upfront'),
};

const policyShape = {
  earlyRepaymentPolicy: z.enum(LOAN_EARLY_REPAYMENT_POLICIES).default('no_rebate'),
  lateFeeType: z.enum(LOAN_LATE_FEE_TYPES).default('none'),
  /** An amount for `fixed`, a percentage for `percent`. */
  lateFeeValue: z.union([z.string(), z.number()]).transform((v) => String(v).trim()).default('0'),
  /** Days after the due date before a late fee may be applied. */
  lateFeeGraceDays: z.coerce.number().int().min(0).max(365).default(0),
};

function policyIsCoherent(
  v: { lateFeeType: LoanLateFeeType; lateFeeValue: string; processingFee?: string; principal?: string },
  ctx: z.RefinementCtx,
): void {
  if (v.lateFeeType === 'fixed' && !(DECIMAL_MONEY_RE.test(v.lateFeeValue) && Number(v.lateFeeValue) > 0)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['lateFeeValue'], message: 'Enter the late fee amount' });
  }
  if (v.lateFeeType === 'percent' && !(RATE_RE.test(v.lateFeeValue) && Number(v.lateFeeValue) > 0 && Number(v.lateFeeValue) <= 100)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['lateFeeValue'], message: 'Enter a percentage between 0 and 100' });
  }
  if (v.processingFee && v.principal && Number(v.processingFee) >= Number(v.principal)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['processingFee'], message: 'The fee must be less than the amount lent' });
  }
}

const DECIMAL_MONEY_RE = /^\d{1,16}(\.\d{1,2})?$/;

function termsAreCoherent(
  v: {
    interestType: LoanInterestType;
    interestRate: string;
    frequency: LoanFrequency;
    intervalDays?: number | null | undefined;
    issuedOn: string;
    firstDueOn: string;
  },
  ctx: z.RefinementCtx,
): void {
  if (v.frequency === 'custom' && !v.intervalDays) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['intervalDays'], message: 'Say how many days apart the instalments are' });
  }
  if (v.interestType !== 'none' && rateScaled(v.interestRate) === 0n) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['interestRate'], message: 'Enter the interest rate, or choose Interest-free' });
  }
  if (v.firstDueOn < v.issuedOn) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['firstDueOn'], message: 'The first repayment cannot be before the loan is issued' });
  }
}

const loanBaseShape = {
  direction: z.enum(LOAN_DIRECTIONS).default('receivable'),
  category: z.enum(LOAN_CATEGORIES),
  counterpartyKind: z.enum(LOAN_COUNTERPARTY_KINDS),
  counterpartyName: shortTextSchema,
  counterpartyPhone: phoneSchema.nullable().optional(),
  riderId: uuidSchema.nullable().optional(),
  staffId: uuidSchema.nullable().optional(),
  branchId: uuidSchema.nullable().optional(),
  purpose: shortTextSchema.nullable().optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).default('cash'),
  /** The cash or bank account the loan is paid out of (or, borrowed, paid into). Null: the default for the method. */
  accountId: uuidSchema.nullable().optional(),
  notes: longTextSchema.nullable().optional(),
  /** Id returned by POST /loans/documents. */
  attachmentId: uuidSchema.nullable().optional(),
  ...termsShape,
  ...policyShape,
};

function linksAreCoherent(
  v: {
    direction: LoanDirection;
    counterpartyKind: LoanCounterpartyKind;
    riderId?: string | null | undefined;
    staffId?: string | null | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  if (v.counterpartyKind === 'rider' && !v.riderId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['riderId'], message: 'Choose the rider' });
  }
  if (v.counterpartyKind === 'staff' && !v.staffId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['staffId'], message: 'Choose the staff member' });
  }
  if (v.counterpartyKind !== 'rider' && v.riderId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['riderId'], message: 'Only a rider borrower links to a rider' });
  }
  if (v.counterpartyKind !== 'staff' && v.staffId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['staffId'], message: 'Only a staff borrower links to a staff record' });
  }
  // The business borrows from lenders, not from its own riders and staff.
  if (v.direction === 'payable' && (v.counterpartyKind === 'rider' || v.counterpartyKind === 'staff')) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['counterpartyKind'], message: 'A lender is an individual or an organisation' });
  }
}

export const loanInputSchema = z
  .object({
    ...loanBaseShape,
    /** `draft` saves the terms only; `active` issues the loan now. */
    status: z.enum(['draft', 'active']).default('active'),
    /** Generated by the form once; a second submission returns the first loan instead of making another. */
    idempotencyKey: z.string().trim().min(8).max(100).optional(),
  })
  .superRefine((v, ctx) => {
    termsAreCoherent(v, ctx);
    linksAreCoherent(v, ctx);
    policyIsCoherent(v, ctx);
  });
export type LoanInput = z.infer<typeof loanInputSchema>;

/**
 * Editing. A draft may change anything. An issued loan may change only what
 * does not alter what is owed — its terms are a record of what was agreed, and
 * a correction to them is a cancellation and a new loan.
 */
export const loanUpdateSchema = z
  .object({
    category: z.enum(LOAN_CATEGORIES).optional(),
    counterpartyKind: z.enum(LOAN_COUNTERPARTY_KINDS).optional(),
    counterpartyName: shortTextSchema.optional(),
    counterpartyPhone: phoneSchema.nullable().optional(),
    riderId: uuidSchema.nullable().optional(),
    staffId: uuidSchema.nullable().optional(),
    branchId: uuidSchema.nullable().optional(),
    purpose: shortTextSchema.nullable().optional(),
    paymentMethod: z.enum(PAYMENT_METHODS).optional(),
    accountId: uuidSchema.nullable().optional(),
    notes: longTextSchema.nullable().optional(),
    attachmentId: uuidSchema.nullable().optional(),
    principal: positiveMoneySchema.optional(),
    interestType: z.enum(LOAN_INTEREST_TYPES).optional(),
    interestRate: ratePercentSchema.optional(),
    frequency: z.enum(LOAN_FREQUENCIES).optional(),
    intervalDays: z.coerce.number().int().min(1).max(365).nullable().optional(),
    instalmentCount: z.coerce.number().int().min(1).max(MAX_LOAN_INSTALMENTS).optional(),
    issuedOn: isoDateSchema.optional(),
    firstDueOn: isoDateSchema.optional(),
    direction: z.enum(LOAN_DIRECTIONS).optional(),
    processingFee: nonNegativeMoneySchema.optional(),
    feeCollection: z.enum(LOAN_FEE_COLLECTIONS).optional(),
    earlyRepaymentPolicy: z.enum(LOAN_EARLY_REPAYMENT_POLICIES).optional(),
    lateFeeType: z.enum(LOAN_LATE_FEE_TYPES).optional(),
    lateFeeValue: z.union([z.string(), z.number()]).transform((v) => String(v).trim()).optional(),
    lateFeeGraceDays: z.coerce.number().int().min(0).max(365).optional(),
  })
  .strict();
export type LoanUpdate = z.infer<typeof loanUpdateSchema>;

/** The fields an issued loan still lets you change. */
export const LOAN_FIELDS_EDITABLE_WHEN_ISSUED = [
  'counterpartyName',
  'counterpartyPhone',
  'purpose',
  'notes',
  'attachmentId',
] as const;

/** For the preview endpoint and the form: just the terms. */
export const loanTermsSchema = z.object(termsShape).superRefine(termsAreCoherent);
export type LoanTermsInput = z.infer<typeof loanTermsSchema>;

export const loanRepaymentInputSchema = z.object({
  amount: positiveMoneySchema,
  paidOn: isoDateSchema,
  paymentMethod: z.enum(PAYMENT_METHODS).default('cash'),
  transactionReference: shortTextSchema.nullable().optional(),
  notes: longTextSchema.nullable().optional(),
  /** The cash or bank account the money landed in. Null: the default for the method. */
  accountId: uuidSchema.nullable().optional(),
  /**
   * Generated by the client once per form, and sent again on a retry. The same
   * key never records a second payment.
   */
  idempotencyKey: z.string().trim().min(8).max(100),
  /**
   * The same amount, date, method and reference as a live repayment on this
   * loan is refused as a likely duplicate unless this says it is deliberate.
   */
  confirmDuplicate: z.boolean().optional(),
});
export type LoanRepaymentInput = z.infer<typeof loanRepaymentInputSchema>;

/**
 * Lend more on a loan already out.
 *
 * `paymentMethod: 'credit'` records an amount that was lent without money
 * moving now — the correction of a principal keyed too low — and so touches
 * no cash or bank balance. Every other method is money paid out.
 */
export const loanTopUpInputSchema = z.object({
  amount: positiveMoneySchema,
  occurredOn: isoDateSchema,
  /** When the added amount is due back. One instalment, interest-free. */
  dueOn: isoDateSchema,
  paymentMethod: z.enum(PAYMENT_METHODS).default('cash'),
  accountId: uuidSchema.nullable().optional(),
  transactionReference: shortTextSchema.nullable().optional(),
  notes: z.string().trim().min(3, 'Say why more is being lent').max(500),
  idempotencyKey: z.string().trim().min(8).max(100),
});
export type LoanTopUpInput = z.infer<typeof loanTopUpInputSchema>;

/** Reduce what is owed to correct a wrong entry. No money moves. */
export const loanAdjustmentInputSchema = z.object({
  amount: positiveMoneySchema,
  occurredOn: isoDateSchema,
  reason: z.string().trim().min(3, 'Say what is being corrected').max(500),
  idempotencyKey: z.string().trim().min(8).max(100),
});
export type LoanAdjustmentInput = z.infer<typeof loanAdjustmentInputSchema>;

export const loanSettlementInputSchema = z.object({
  /** Must equal the quote; refused if anything changed since it was shown. */
  amount: positiveMoneySchema,
  paidOn: isoDateSchema,
  paymentMethod: z.enum(PAYMENT_METHODS).default('cash'),
  accountId: uuidSchema.nullable().optional(),
  transactionReference: shortTextSchema.nullable().optional(),
  notes: longTextSchema.nullable().optional(),
  idempotencyKey: z.string().trim().min(8).max(100),
});
export type LoanSettlementInput = z.infer<typeof loanSettlementInputSchema>;

export const loanLateFeeInputSchema = z.object({
  instalmentId: uuidSchema,
  idempotencyKey: z.string().trim().min(8).max(100),
});

export const loanChargeWaiverInputSchema = z.object({
  reason: z.string().trim().min(3, 'Say why the fee is being waived').max(500),
});

export const loanReversalInputSchema = z.object({
  reason: z.string().trim().min(3, 'Say why the payment is being reversed').max(500),
  idempotencyKey: z.string().trim().min(8).max(100),
});
export type LoanReversalInput = z.infer<typeof loanReversalInputSchema>;

export const loanCloseInputSchema = z.object({
  reason: z.string().trim().min(3, 'A reason is required').max(500),
  /** Defaults to today in the business timezone. */
  on: isoDateSchema.optional(),
});
export type LoanCloseInput = z.infer<typeof loanCloseInputSchema>;

export const loanDeductionAgreementInputSchema = z.object({
  /** The most that may be deducted from any one remittance or payout. */
  amountPerDeduction: positiveMoneySchema,
  sources: z.array(z.enum(LOAN_DEDUCTION_SOURCES)).min(1).max(2),
  /** When the rider agreed. */
  agreedOn: isoDateSchema,
  /** A signed form, if there is one. Id returned by POST /loans/documents. */
  attachmentId: uuidSchema.nullable().optional(),
  notes: longTextSchema.nullable().optional(),
});
export type LoanDeductionAgreementInput = z.infer<typeof loanDeductionAgreementInputSchema>;

export const loanDeductionInputSchema = z.object({
  source: z.enum(LOAN_DEDUCTION_SOURCES),
  /** The remittance payment or rider settlement the money is deducted from. */
  sourceId: uuidSchema,
  amount: positiveMoneySchema,
  idempotencyKey: z.string().trim().min(8).max(100),
  notes: longTextSchema.nullable().optional(),
});
export type LoanDeductionInput = z.infer<typeof loanDeductionInputSchema>;

export const loanPurposeInputSchema = z.object({ name: shortTextSchema.max(80) });

export const LOAN_SORTS = ['issuedOn', 'reference', 'principal', 'outstanding', 'nextDueOn', 'counterpartyName'] as const;
export type LoanSort = (typeof LOAN_SORTS)[number];

export const loanFiltersSchema = paginationSchema
  .merge(dateRangeFields)
  .extend({
    direction: z.enum(LOAN_DIRECTIONS).default('receivable'),
    category: z.enum(LOAN_CATEGORIES).optional(),
    status: z.enum(LOAN_STATUSES).optional(),
    branchId: uuidSchema.optional(),
    riderId: uuidSchema.optional(),
    staffId: uuidSchema.optional(),
    q: z.string().trim().max(100).optional(),
    currency: z.string().trim().length(3).toUpperCase().optional(),
    sort: z.enum(LOAN_SORTS).default('issuedOn'),
    dir: z.enum(['asc', 'desc']).default('desc'),
  })
  .refine(isOrderedRange, { message: ORDERED_RANGE_ISSUE.message, path: ['from'] });
export type LoanFilters = z.infer<typeof loanFiltersSchema>;

export const loanReportFiltersSchema = dateRangeFields
  .extend({
    direction: z.enum(LOAN_DIRECTIONS).default('receivable'),
    category: z.enum(LOAN_CATEGORIES).optional(),
    branchId: uuidSchema.optional(),
    currency: z.string().trim().length(3).toUpperCase().optional(),
  })
  .refine(isOrderedRange, { message: ORDERED_RANGE_ISSUE.message, path: ['from'] });
export type LoanReportFilters = z.infer<typeof loanReportFiltersSchema>;

// ── Views ──────────────────────────────────────────────────────────────────

export const loanListItemSchema = z.object({
  id: uuidSchema,
  reference: z.string(),
  direction: z.enum(LOAN_DIRECTIONS),
  category: z.enum(LOAN_CATEGORIES),
  counterpartyKind: z.enum(LOAN_COUNTERPARTY_KINDS),
  counterpartyName: z.string(),
  counterpartyPhone: z.string().nullable(),
  riderId: uuidSchema.nullable(),
  staffId: uuidSchema.nullable(),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),
  purpose: z.string().nullable(),
  currencyCode: z.string(),
  /** The amount lent when the loan was issued. Never changes. */
  principal: moneySchema,
  /** Further amounts lent on this loan since, net of any reversed. */
  additionalAmounts: moneySchema,
  /** Balance corrections that reduced what is owed. Not repayments, not write-offs. */
  adjustments: moneySchema,
  totalInterest: moneySchema,
  /** Processing and late fees charged so far. */
  totalFees: moneySchema,
  totalPayable: moneySchema,
  /** Principal, interest and fees received (or, when borrowing, paid). */
  repaid: moneySchema,
  principalRepaid: moneySchema,
  interestRepaid: moneySchema,
  feesRepaid: moneySchema,
  /** Interest waived on early settlement under the loan's policy. */
  interestRebated: moneySchema,
  /** Principal not yet repaid. Zero once written off. */
  outstanding: moneySchema,
  /** Principal and interest still owed, the figure a payment is checked against. */
  balanceOwed: moneySchema,
  overdueAmount: moneySchema,
  writtenOff: moneySchema,
  status: z.enum(LOAN_STATUSES),
  issuedOn: z.string(),
  nextDueOn: z.string().nullable(),
  nextDueAmount: moneySchema.nullable(),
  interestType: z.enum(LOAN_INTEREST_TYPES),
  interestRate: z.string(),
  frequency: z.enum(LOAN_FREQUENCIES),
  instalmentCount: z.number().int(),
  hasDeductionAgreement: z.boolean(),
});
export type LoanListItem = z.infer<typeof loanListItemSchema>;

export interface LoanList {
  data: LoanListItem[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
  /** Currencies present for this direction, so the screen can offer a switch. */
  currencies: string[];
  currencyCode: string;
}

export const loanInstalmentViewSchema = z.object({
  id: uuidSchema,
  sequence: z.number().int(),
  dueOn: z.string(),
  principal: moneySchema,
  interest: moneySchema,
  fee: moneySchema,
  total: moneySchema,
  principalPaid: moneySchema,
  interestPaid: moneySchema,
  feePaid: moneySchema,
  writtenOff: moneySchema,
  rebated: moneySchema,
  remaining: moneySchema,
  state: z.enum(['paid', 'partial', 'due', 'overdue', 'upcoming', 'written_off']),
});
export type LoanInstalmentView = z.infer<typeof loanInstalmentViewSchema>;

export const loanTransactionViewSchema = z.object({
  id: uuidSchema,
  kind: z.enum(LOAN_TRANSACTION_KINDS),
  amount: moneySchema,
  principal: moneySchema,
  interest: moneySchema,
  fee: moneySchema,
  occurredOn: z.string(),
  paymentMethod: z.string().nullable(),
  transactionReference: z.string().nullable(),
  accountName: z.string().nullable(),
  source: z.enum(LOAN_REPAYMENT_SOURCES),
  sourceId: uuidSchema.nullable(),
  notes: z.string().nullable(),
  reversesId: uuidSchema.nullable(),
  /** Set on an entry that a later reversal undid. */
  reversedById: uuidSchema.nullable(),
  recordedByName: z.string().nullable(),
  createdAt: z.string(),
});
export type LoanTransactionView = z.infer<typeof loanTransactionViewSchema>;

export const loanDeductionAgreementViewSchema = z.object({
  id: uuidSchema,
  amountPerDeduction: moneySchema,
  sources: z.array(z.enum(LOAN_DEDUCTION_SOURCES)),
  agreedOn: z.string(),
  authorisedByName: z.string().nullable(),
  attachmentId: uuidSchema.nullable(),
  notes: z.string().nullable(),
  revokedAt: z.string().nullable(),
  revokedReason: z.string().nullable(),
  createdAt: z.string(),
});
export type LoanDeductionAgreementView = z.infer<typeof loanDeductionAgreementViewSchema>;

export interface LoanChargeView {
  id: string;
  instalmentId: string;
  sequence: number;
  kind: LoanChargeKind;
  amount: string;
  assessedOn: string;
  waived: boolean;
  waivedReason: string | null;
  recordedByName: string | null;
}

export interface LoanView extends LoanListItem {
  processingFee: string;
  feeCollection: LoanFeeCollection;
  earlyRepaymentPolicy: LoanEarlyRepaymentPolicy;
  lateFeeType: LoanLateFeeType;
  lateFeeValue: string;
  lateFeeGraceDays: number;
  charges: LoanChargeView[];
  /** Instalments a late fee may be applied to today, with the fee it would be. */
  lateFeeCandidates: Array<{ instalmentId: string; sequence: number; dueOn: string; fee: string }>;
  intervalDays: number | null;
  firstDueOn: string;
  finalDueOn: string | null;
  paymentMethod: string;
  notes: string | null;
  attachmentId: string | null;
  riderName: string | null;
  staffName: string | null;
  createdByName: string | null;
  createdAt: string;
  closedReason: string | null;
  closedOn: string | null;
  /** Draft only: the schedule the loan WOULD have if issued now. */
  instalments: LoanInstalmentView[];
  transactions: LoanTransactionView[];
  deductionAgreement: LoanDeductionAgreementView | null;
}

/** A rider or staff member, found by name, phone or ID, to borrow. */
export interface LoanBorrowerMatch {
  kind: 'rider' | 'staff';
  id: string;
  name: string;
  phone: string | null;
  /** Rider ID or job title, whichever the record has. */
  detail: string | null;
  branchId: string | null;
}

/** A remittance payment or payout a rider loan may be deducted from. */
export interface LoanDeductionSourceView {
  source: LoanDeductionSource;
  id: string;
  occurredOn: string;
  label: string;
  amount: string;
  /** Already deducted against, by this loan or another. */
  deducted: boolean;
}

export interface LoanSummaryFigure {
  amount: string;
  count: number;
}

export interface LoanSummary {
  direction: LoanDirection;
  currencyCode: string;
  currencies: string[];
  /** Principal issued (excluding cancelled loans). */
  totalLoaned: LoanSummaryFigure;
  /** Principal repaid. */
  totalRepaid: LoanSummaryFigure;
  /** Principal still owed. */
  outstanding: LoanSummaryFigure;
  /** Principal and interest past due and unpaid. */
  overdue: LoanSummaryFigure;
  /** Outstanding principal by category, for the donut. */
  byCategory: Array<{ category: LoanCategory; amount: string; count: number }>;
  recentRepayments: Array<{
    id: string;
    loanId: string;
    reference: string;
    counterpartyName: string;
    occurredOn: string;
    amount: string;
    paymentMethod: string | null;
    source: LoanRepaymentSource;
    reversed: boolean;
  }>;
}

export interface LoanReport {
  direction: LoanDirection;
  currencyCode: string;
  from: string;
  to: string;
  disbursements: { amount: string; count: number };
  collections: { amount: string; principal: string; interest: string; count: number };
  /** Interest received on lending. Finance income, never revenue. */
  interestEarned: string;
  /** Interest paid on borrowing. Finance expense, never an operating expense. */
  interestPaid: string;
  /** Processing and late fees received on lending. Finance income. */
  feesEarned: string;
  /** Fees paid to lenders. Finance expense. */
  feesPaid: string;
  /** Interest waived on early settlement in the window. */
  interestRebated: string;
  /** Principal written off in the window: the bad-debt expense. */
  badDebt: string;
  writeOffs: { amount: string; count: number };
  outstandingPrincipal: string;
  outstandingByCategory: Array<{ category: LoanCategory; amount: string; count: number }>;
  overdueInstalments: Array<{
    loanId: string;
    reference: string;
    counterpartyName: string;
    sequence: number;
    dueOn: string;
    daysLate: number;
    remaining: string;
  }>;
  /** Overdue balance by how late it is. */
  ageing: Array<{ bucket: '1-30' | '31-60' | '61-90' | '90+'; amount: string; instalments: number }>;
  /** Instalments falling due in the window, and how they were met. */
  performance: {
    due: number;
    paidOnTime: number;
    paidLate: number;
    unpaid: number;
    /** paidOnTime / due, one decimal, null when nothing fell due. */
    onTimeRate: number | null;
  };
  /**
   * Cash movements, kept apart from profit. Out is money leaving the business:
   * a loan issued, or a lender repaid.
   */
  cashFlow: { inflow: string; outflow: string; net: string };
}

/** Which money words fit the direction, so labels never say "repaid" of a debt the business owes. */
export function loanWording(direction: LoanDirection) {
  return direction === 'receivable'
    ? {
        counterparty: 'Borrower',
        loaned: 'Total loaned',
        repaid: 'Total repaid',
        repayment: 'Repayment received',
        record: 'Record repayment',
        interest: 'Interest earned',
      }
    : {
        counterparty: 'Lender',
        loaned: 'Total borrowed',
        repaid: 'Total paid back',
        repayment: 'Payment to lender',
        record: 'Record payment',
        interest: 'Interest paid',
      };
}
